import { Component, Inject, Injector, OnDestroy, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { DocumentNode, ProcedureDocument } from '@completion/models';
import { NodeFactoryService } from '../../node-factory.service';
import { Store } from '@ngrx/store';
import { selectEditMode, getCurrentDocument } from '@completion/selectors';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { DocumentUpdate } from '@completion/actions';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddComment } from '@completion/actions';
import { selectReviewerMode, getCurrentUser } from '@completion/selectors';
import { User } from '@completion/models';
import { Comment as ReviewComment } from '@completion/models';
import { ReviewCommentDialogComponent } from '../../review-comments/review-comment-dialog/review-comment-dialog.component';
import { persistNodeUpdateForNode } from '../node-persistence.util';

@Component({
  selector: 'app-paragraph',
  templateUrl: './paragraph.component.html',
  styleUrls: ['./paragraph.component.scss'],
  standalone: false,
})
export class ParagraphComponent implements OnDestroy {
  documentNode: DocumentNode;
  editMode: boolean = false;
  // Local edit buffer to avoid mutating readonly store objects directly
  localContent: string = '';
  // Expose the full ProcedureDocument for child injector creation in the template
  procedureDocument: ProcedureDocument | null = null;
  trustedHtml: SafeHtml = '';
  contentControl = new FormControl('');
  private contentDirty = false;

  private readonly destroy$ = new Subject();

  reviewerMode = false;
  currentUser: User | null = null;
  selectedText: string | null = null;
  selectionStart: number | null = null;
  selectionEnd: number | null = null;
  selectionButtonPosition: { top: number; left: number } | null = null;

  private lastPointer: { x: number; y: number } | null = null;

  private currentComments: any[] = [];

  constructor(
    public nodeFactory: NodeFactoryService,
    public injector: Injector,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    @Inject('documentNode') documentNode: DocumentNode,
    // The full ProcedureDocument is provided by NodeFactoryService under the 'node' token
    @Inject('node') procedureDocument: ProcedureDocument,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private el: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {
    this.documentNode = documentNode;
    this.procedureDocument = procedureDocument;
    this.localContent = this.documentNode?.content ?? '';

    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.editMode = mode;
      });
    this.contentControl.setValue(this.localContent, { emitEvent: false });
    this.contentControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      // Only buffer locally; do not push to store yet
      this.localContent = val ?? '';
      this.contentDirty = true;
    });
    this.updateTrustedHtml();

    // Initialize comments from the provided node to render highlights immediately
    this.currentComments = Array.isArray((this.documentNode as any)?.comments) ? ((this.documentNode as any).comments as any[]) : [];
    // Rebuild trusted HTML with highlights
    this.updateTrustedHtml();

    // Track reviewer mode and user
    this.store
      .select(selectReviewerMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.reviewerMode = m));
    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => (this.currentUser = u as User));

    // Subscribe to document changes to pick up latest comments for this node and refresh highlights
    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        const nodeId = this.documentNode?.id;
        const signatureRef = JSON.stringify({
          name: this.documentNode?.name,
          type: this.documentNode?.type,
          content: this.documentNode?.content,
        });
        let latestNode: DocumentNode | null = null;
        const findNode = (nodes?: DocumentNode[]) => {
          if (!nodes) return;
          for (const n of nodes) {
            if (!n) continue;
            if (nodeId != null) {
              if (String(n.id) === String(nodeId)) {
                latestNode = n;
                return;
              }
            } else {
              const nodeSignature = JSON.stringify({ name: n.name, type: n.type, content: n.content });
              if (nodeSignature === signatureRef) {
                latestNode = n;
                return;
              }
            }
            if (n?.children?.length) findNode(n.children);
            if (latestNode) return;
          }
        };
        findNode(doc?.nodes);
        const nextComments =
          latestNode && Array.isArray(latestNode.comments)
            ? latestNode.comments
            : Array.isArray((this.documentNode as any)?.comments)
              ? (this.documentNode as any).comments
              : [];
        this.currentComments = nextComments as any[];
        // Rebuild trusted HTML with highlights after comment change
        this.updateTrustedHtml();
      });
  }

  onEditorChange(_evt?: any): void {
    // Intentionally empty: FormControl valueChanges already handles buffering.
  }

  // Persist content change immutably
  commitEditor(): void {
    if (!this.contentDirty) {
      return;
    }
    this.contentDirty = false;
    if (!this.documentNode) {
      return;
    }
    const newContent = this.localContent ?? '';
    if (newContent === this.documentNode.content) {
      return;
    } // nothing changed structurally

    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((latest) => {
        if (!latest) {
          return;
        }
        const cloned: ProcedureDocument = JSON.parse(JSON.stringify(latest));
        const targetId = this.documentNode.id;
        const signature = JSON.stringify({
          name: this.documentNode.name,
          type: this.documentNode.type,
          content: this.documentNode.content,
        });

        const updateById = (nodes: DocumentNode[] | undefined): boolean => {
          if (!nodes) {
            return false;
          }
          for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            const nodeSignature = JSON.stringify({ name: n.name, type: n.type, content: n.content });
            if (n.id === targetId || (targetId == null && nodeSignature === signature)) {
              n.content = newContent;
              return true;
            }
            if (n.children && updateById(n.children)) {
              return true;
            }
          }
          return false;
        };

        if (updateById(cloned.nodes)) {
          this.updateQueue.queue(cloned);
          // Update display HTML after commit without mutating frozen store node
          this.updateTrustedHtml(newContent);
        }
      });
  }

  // Delete this paragraph node (top-level or nested)
  deleteParagraph(): void {
    if (!this.documentNode) {
      return;
    }
    const targetId = this.documentNode.id;
    const signature = JSON.stringify({ name: this.documentNode.name, type: this.documentNode.type, content: this.documentNode.content });

    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((latest) => {
        if (!latest) {
          return;
        }
        const cloned: ProcedureDocument = JSON.parse(JSON.stringify(latest));

        const removeById = (nodes: DocumentNode[] | undefined): boolean => {
          if (!nodes) {
            return false;
          }
          for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            const nodeSignature = JSON.stringify({ name: n.name, type: n.type, content: n.content });
            if (n.id === targetId || (targetId == null && nodeSignature === signature)) {
              nodes.splice(i, 1);
              return true;
            }
            if (n.children && removeById(n.children)) {
              return true;
            }
          }
          return false;
        };

        if (removeById(cloned.nodes)) {
          this.store.dispatch(new DocumentUpdate(cloned));
        }
      });
  }

  @HostListener('mousedown', ['$event'])
  onHostMouseDown(evt: MouseEvent): void {
    this.lastPointer = { x: evt.clientX, y: evt.clientY };
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(evt: MouseEvent): void {
    if (evt.buttons & 1) {
      this.lastPointer = { x: evt.clientX, y: evt.clientY };
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(evt: MouseEvent): void {
    this.onMouseUpCaptureSelection(evt);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.selectionButtonPosition) {
      this.clearSelection();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.selectionButtonPosition) {
      this.clearSelection();
    }
  }

  onMouseUpCaptureSelection(_evt: MouseEvent): void {
    if (!this.canShowParagraphButton) {
      this.clearSelection();
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      this.clearSelection();
      return;
    }
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      this.clearSelection();
      return;
    }

    const hostEl = this.el?.nativeElement;
    const displayEl = hostEl?.querySelector?.('.paragraph-display') || hostEl;
    if (displayEl && !displayEl.contains(range.commonAncestorContainer)) {
      this.clearSelection();
      return;
    }

    // Use raw selection text and ensure it has at least one non-whitespace character (including NBSP handling)
    const rawSelected = sel.toString();
    // Debug: log the raw selected text
    const hasNonWhitespace = /[^\s\u00A0]/.test(rawSelected);
    if (!rawSelected || !hasNonWhitespace) {
      this.clearSelection();
      return;
    }

    // Compute text offsets
    const walker = document.createTreeWalker(displayEl, NodeFilter.SHOW_TEXT, null);
    let pos = 0;
    let startOffset: number | null = null;
    let endOffset: number | null = null;
    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const nodeText = node.textContent || '';
      if (node === range.startContainer) {
        startOffset = pos + range.startOffset;
      }
      if (node === range.endContainer) {
        endOffset = pos + range.endOffset;
      }
      pos += nodeText.length;
      if (startOffset !== null && endOffset !== null) break;
    }
    if (startOffset === null || endOffset === null) {
      this.clearSelection();
      return;
    }

    // Button placement in viewport coordinates (position: fixed) — always anchor to selection rect
    const btnSize = 40;
    const margin = 8;
    const vw = window.innerWidth || document.documentElement.clientWidth || 1024;
    const vh = window.innerHeight || document.documentElement.clientHeight || 768;

    const clientRects = (range as any).getClientRects?.() as DOMRectList | DOMRect[] | undefined;
    let rect: DOMRect | undefined;
    if (clientRects && (clientRects as any).length) {
      const list = Array.from(clientRects as any as DOMRect[]);
      rect = list[list.length - 1];
    }
    if (!rect) {
      rect = range.getBoundingClientRect();
    }
    let left = rect.right + margin;
    const top = rect.top + rect.height / 2 - btnSize / 2;

    if (left + btnSize > vw) {
      left = left - btnSize - margin * 2;
    }
    const clampedLeft = Math.max(0, Math.min(left, vw - btnSize));
    const clampedTop = Math.max(0, Math.min(top, vh - btnSize));

    this.selectedText = rawSelected;
    this.selectionStart = startOffset;
    this.selectionEnd = endOffset;
    this.selectionButtonPosition = { top: clampedTop, left: clampedLeft };

    this.lastPointer = null;
    try {
      this.cdr.detectChanges();
    } catch {}
  }

  private clearSelection(): void {
    this.selectedText = null;
    this.selectionStart = null;
    this.selectionEnd = null;
    this.selectionButtonPosition = null;
    try {
      this.cdr.detectChanges();
    } catch {}
  }

  private updateTrustedHtml(newContent?: string): void {
    const content = newContent !== undefined ? newContent : this.localContent;
    const sanitized = DOMPurify.sanitize(content);

    // Build a detached container to apply highlights safely on the sanitized HTML
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sanitized;

    const fullText = wrapper.textContent ?? '';
    const textLen = fullText.length;

    // Utility: normalize text replacing NBSP (\u00A0), any whitespace, and literal "&nbsp;" with single spaces,
    // producing a normalized string and a map from normalized indices to raw indices.
    const normalizeWithMap = (input: string) => {
      const map: number[] = [];
      let out = '';
      for (let i = 0; i < input.length; i++) {
        // Handle literal entity text
        if (input.startsWith('&nbsp;', i)) {
          if (out.length === 0 || out[out.length - 1] !== ' ') {
            out += ' ';
            map.push(i);
          }
          i += 5; // skip the rest of '&nbsp;'
          continue;
        }
        const ch = input[i];
        const isSpace = /\s/.test(ch) || ch === '\u00A0';
        if (isSpace) {
          if (out.length === 0 || out[out.length - 1] !== ' ') {
            out += ' ';
            map.push(i);
          }
        } else {
          out += ch.toLowerCase();
          map.push(i);
        }
      }
      return { norm: out, map };
    };

    // Prepare normalized comment ranges from currentComments relative to fullText
    const ranges = (this.currentComments || [])
      .map((c: any, idx: number) => {
        const commentId = (c && (c.id ?? c.commentId)) ?? null;
        // Try numeric offsets via multiple possible property names
        let s = Number(c?.startOffset ?? c?.selectionStart ?? c?.start ?? c?.rangeStart ?? c?.selection?.start ?? c?.range?.start);
        let e = Number(c?.endOffset ?? c?.selectionEnd ?? c?.end ?? c?.rangeEnd ?? c?.selection?.end ?? c?.range?.end);
        const tryResolveFromSelection = () => {
          const raw = (c?.selectionText ?? '').toString();
          const selTrim = raw
            .replace(/\u00A0/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          if (!selTrim) return false;
          const hayRaw = fullText;
          // 1) direct exact match
          let idx2 = hayRaw.indexOf(raw);
          if (idx2 >= 0) {
            s = idx2;
            e = idx2 + raw.length;
            return true;
          }
          // 2) case-insensitive match
          const hayLower = hayRaw.toLowerCase();
          const needleLower = raw.toLowerCase();
          idx2 = hayLower.indexOf(needleLower);
          if (idx2 >= 0) {
            s = idx2;
            e = idx2 + raw.length;
            return true;
          }
          // 3) NBSP/whitespace and literal &nbsp; normalized match
          const { norm: normHay, map } = normalizeWithMap(hayRaw);
          const { norm: normNeedle } = normalizeWithMap(selTrim);
          const normIdx = normHay.indexOf(normNeedle);
          if (normIdx >= 0) {
            const rawStart = map[normIdx] ?? 0;
            const rawEnd = map[normIdx + normNeedle.length - 1] ?? rawStart;
            s = rawStart;
            e = rawEnd + 1;
            return true;
          }
          return false;
        };
        if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) {
          tryResolveFromSelection();
        }
        if (!Number.isFinite(s) || !Number.isFinite(e)) {
          return null;
        }
        let start = Math.min(s, e);
        let end = Math.max(s, e);
        start = Math.max(0, Math.min(start, textLen));
        end = Math.max(0, Math.min(end, textLen));
        if (end <= start) return null;
        return { start, end, id: commentId, index: idx };
      })
      .filter(Boolean) as Array<{ start: number; end: number; id: any; index: number }>;

    if (ranges.length) {
      // Sort by start to apply deterministically
      ranges.sort((a, b) => a.start - b.start);

      const highlightRange = (container: HTMLElement, start: number, end: number, id: any, index: number) => {
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
        let pos = 0;
        const overlaps: { node: Text; from: number; to: number }[] = [];
        while (walker.nextNode()) {
          const node = walker.currentNode as Text;
          const text = node.textContent || '';
          const nodeStart = pos;
          const nodeEnd = pos + text.length;
          const overlapStart = Math.max(start, nodeStart);
          const overlapEnd = Math.min(end, nodeEnd);
          if (overlapStart < overlapEnd) {
            overlaps.push({ node, from: overlapStart - nodeStart, to: overlapEnd - nodeStart });
          }
          pos = nodeEnd;
        }
        for (let i = overlaps.length - 1; i >= 0; i--) {
          const r = overlaps[i];
          const node = r.node;
          const text = node.textContent || '';
          const before = text.slice(0, r.from);
          const mid = text.slice(r.from, r.to);
          const after = text.slice(r.to);
          const span = document.createElement('span');
          span.className = 'comment-highlight';
          if (id != null) {
            span.setAttribute('data-comment-id', String(id));
          }
          // Always set index attribute for reliable mapping
          span.setAttribute('data-comment-index', String(index));
          const strong = document.createElement('strong');
          strong.textContent = mid;
          span.appendChild(strong);
          span.style.backgroundColor = '#f4ecc0';
          span.style.borderRadius = '2px';
          span.style.padding = '0 1px';
          const frag = document.createDocumentFragment();
          if (before) frag.appendChild(document.createTextNode(before));
          frag.appendChild(span);
          if (after) frag.appendChild(document.createTextNode(after));
          node.parentNode?.replaceChild(frag, node);
        }
      };

      for (const r of ranges) {
        try {
          highlightRange(wrapper as unknown as HTMLElement, r.start, r.end, r.id, r.index);
        } catch {}
      }
    }

    // Bind the final HTML with highlights applied
    this.trustedHtml = this.sanitizer.bypassSecurityTrustHtml(wrapper.innerHTML);

    // Ensure view updates synchronously where possible so anchor can find injected spans
    try {
      this.cdr.detectChanges();
    } catch {}

    // Notify any parent anchor that highlights are now applied so it can recompute connectors
    try {
      const host = this.el?.nativeElement as HTMLElement | null;
      if (host) {
        // Defer the event to the next macrotask so Angular has time to render the sanitized HTML into the DOM
        setTimeout(() => {
          try {
            host.dispatchEvent(new CustomEvent('highlights-applied', { bubbles: true }));
          } catch {}
        }, 0);
      }
    } catch {}
  }

  get canShowSelectionButton(): boolean {
    return !!this.selectedText && !!this.selectionButtonPosition && this.canShowParagraphButton;
  }
  get canShowParagraphButton(): boolean {
    return this.reviewerMode && !this.editMode;
  }

  // Open dialog to add a paragraph-level comment (not tied to a text selection)
  openParagraphCommentDialog(): void {
    if (!this.documentNode || !this.documentNode.id) {
      return;
    }
    if (!this.reviewerMode) {
      return;
    }

    const ref = this.dialog.open(ReviewCommentDialogComponent, {
      width: '520px',
      data: { nodeId: this.documentNode.id },
    });

    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((val: string | undefined) => {
        const content = (val ?? '').trim();
        if (!content) {
          return;
        }
        const payload: Partial<ReviewComment> = {
          content,
          createdAt: new Date().toISOString(),
          author: this.currentUser || undefined,
        } as any;
        this.store.dispatch(new AddComment(this.documentNode.id, payload as any));
        // Persist the full document after the comment has been applied to the store
        // The reducer updates the store synchronously so selecting the current document
        // immediately after dispatch will include the new comment; use the shared helper
        // to clone and enqueue the document update without duplicating mutation logic.
        persistNodeUpdateForNode(this.store, this.updateQueue, this.documentNode, () => {});
      });
  }

  // Open dialog to add a comment for the current text selection
  openSelectionCommentDialog(): void {
    if (!this.documentNode || !this.documentNode.id) {
      return;
    }
    if (!this.selectedText || this.selectionStart == null || this.selectionEnd == null) {
      return;
    }
    if (!this.canShowParagraphButton) {
      return;
    }

    // Snapshot current selection to protect against subsequent mouseup handlers clearing it
    const selText = this.selectedText;
    const selStart = this.selectionStart;
    const selEnd = this.selectionEnd;

    const ref = this.dialog.open(ReviewCommentDialogComponent, {
      width: '520px',
      data: { nodeId: this.documentNode.id, selectionText: selText, selectionStart: selStart, selectionEnd: selEnd },
    });

    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((val: string | undefined) => {
        const content = (val ?? '').trim();
        if (!content) {
          this.clearSelection();
          return;
        }
        const payload: Partial<ReviewComment> = {
          content,
          createdAt: new Date().toISOString(),
          author: this.currentUser || undefined,
          selectionText: selText || undefined,
          startOffset: selStart || undefined,
          endOffset: selEnd || undefined,
        } as any;
        this.store.dispatch(new AddComment(this.documentNode.id, payload as any));

        // Persist full document after comment was added in the store
        persistNodeUpdateForNode(this.store, this.updateQueue, this.documentNode, () => {});

        // Clear selection and hide the floating button after creating / closing the dialog
        this.clearSelection();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
